import React, { useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';

import Button from '@/components/Button/Button';
import Container from '@/components/Container/Container';
import PostItem from '@/components/PostItem/PostItem';
import { ACTION } from '@/components/PostList/utils';
import SearchInput from '@/components/SearchInput/SearchInput';
import { getSearchQuery } from '@/graphql/queries';

import styles from './SearchResults.module.sass';

const INITIAL_STATE = ({ pageInfo, list }) => ({
  cursor: pageInfo?.endCursor,
  hasMore: pageInfo?.hasNextPage,
  list,
});

const reducer = (state, { payload, type }) => {
  if (type === ACTION.UPDATE_LIST) {
    const newPosts = payload.edges.map(({ node }) => node);

    return {
      ...state,
      cursor: payload?.pageInfo?.endCursor,
      hasMore: payload?.pageInfo?.hasNextPage,
      list: state.list,
      list: [...state.list, ...newPosts],
    };
  }
};

const SearchResults = ({ pageInfo, results, term }) => {
  const { query } = getSearchQuery({ term });
  const [fetchMore, { loading }] = useLazyQuery(query);
  const [posts, dispatch] = useReducer(
    reducer,
    INITIAL_STATE({ pageInfo, list: results })
  );
  const hasResults = results.length > 0;

  const loadMore = useCallback(async () => {
    const { cursor, hasMore } = posts;
    if (hasMore) {
      const { data } = await fetchMore({
        variables: { after: cursor, count: 12, term },
      });

      dispatch({ payload: data?.posts, type: ACTION.UPDATE_LIST });
    }
  }, [fetchMore, posts, term]);

  return !hasResults ? (
    <div className={styles.searchOnly}>
      <SearchInput />
      <p>
        No results found for <quote>&ldquo;{term}&rdquo;</quote>:(
      </p>
    </div>
  ) : (
    <>
      <h1>Search results for &ldquo;{term}&rdquo;</h1>
      <SearchInput defaultValue={term} />
      <section className={styles.postList}>
        <Container>
          {posts.list?.map((post) => (
            <article className={styles.postItem} key={post.id}>
              <PostItem {...post} />
            </article>
          ))}
          {posts?.hasMore && (
            <div className={styles.loadMore}>
              {loading ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  alt="Loading..."
                  height={44}
                  src="/icons/loading.svg"
                  width={44}
                />
              ) : (
                <Button onClick={loadMore}>I want more</Button>
              )}
            </div>
          )}
        </Container>
      </section>
    </>
  );
};

SearchResults.propTypes = {
  pageInfo: PropTypes.shape({
    endCursor: PropTypes.string,
    hasNextPage: PropTypes.bool,
  }),
  results: PropTypes.array,
  term: PropTypes.string,
};

export default SearchResults;
